import { useMemo, useCallback, useRef, useEffect } from "react";
import { useOptions } from "../../OptionsContext";
import { shouldRoundGazQuantity, shouldUseStrictGazPalletUnits, shouldCountGazByPiece } from "../../utils/gazPalletRules";
import { isCalcDebugEnabled } from "../../utils/calcDebug";
import { packagesFor } from "./insulationQuantityUnits.js";

const normalizeRegionCode = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "МОСКВА" || normalized === "МОСКОВСКАЯ ОБЛАСТЬ") {
    return "МСК";
  }
  if (normalized === "САНКТ-ПЕТЕРБУРГ" || normalized === "ЛЕНИНГРАДСКАЯ ОБЛАСТЬ") {
    return "СПБ";
  }
  return normalized;
};

const isRegionalGikPriceSource = (source, region) => {
  const normalizedRegion = normalizeRegionCode(region);
  const normalizedSource = String(source || "").trim().toUpperCase();
  return ["МСК", "КРД"].includes(normalizedRegion) &&
    normalizedSource.includes(`ПРАЙС ГИК ${normalizedRegion}`);
};

const isRegionalGikPriceListSource = (source) => {
  const normalizedSource = String(source || "").trim().toUpperCase();
  return normalizedSource.includes("ПРАЙС ГИК МСК") || normalizedSource.includes("ПРАЙС ГИК КРД");
};

const useProductsLogic = (products, setProducts, paymentMethod, options, region) => {
  useOptions();
  const debounceTimeoutsRef = useRef(new Map());

  const NUMBER_PER_PALLET_CATEGORIES = new Set([
    "бордюр",
    "бордюр дорожный",
    "плитка тротуарная",
    "газонная решетка",
    "лоток водоотводный жби",
    "лоток водоотводный тротуарный",
  ]);
  const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
    "бордюр",
    "бордюр дорожный",
    "брусчатка",
    "брусчатка и тротуарная плитка",
    "плитка тротуарная",
    "газонная решетка",
    "лоток водоотводный жби",
    "лоток водоотводный тротуарный",
  ]);

  const shouldUseGazDeliveryCategoryByRegion = (currentRegion) => (
    ["МСК", "КРД"].includes(normalizeRegionCode(currentRegion))
  );

  const isGazobetonCategory = (value, currentRegion = region) => {
    if (typeof value !== "string") {
      return false;
    }

    const normalized = value.trim().toLowerCase();
    if (normalized.includes("газобетон")) {
      return true;
    }

    if (!GAZ_DELIVERY_CATEGORY_NAMES.has(normalized)) {
      return false;
    }

    return shouldUseGazDeliveryCategoryByRegion(currentRegion);
  };

  const isNumberPerPalletCategory = (selectedData = {}, product = {}) => {
    const category = String(selectedData.CATEGORY || product.product_group || "").trim().toLowerCase();
    const calculator = String(selectedData.CALCULATOR || product.category || "").trim().toLowerCase();

    return (
      NUMBER_PER_PALLET_CATEGORIES.has(category) ||
      NUMBER_PER_PALLET_CATEGORIES.has(calculator)
    ) && isRegionalGikPriceListSource(selectedData.SOURCE || product.source);
  };

  const isGazDeliveryProduct = (selectedData = {}, product = {}) => {
    const data = selectedData || {};
    const currentProduct = product || {};
    return (
      isGazobetonCategory(data.CATEGORY) ||
      isGazobetonCategory(data.CALCULATOR) ||
      isGazobetonCategory(currentProduct.product_group) ||
      isGazobetonCategory(currentProduct.category) ||
      isRegionalGikPriceSource(data.SOURCE || currentProduct.source, region)
    );
  };

  const shouldRoundGazProductQuantity = (selectedData = {}, product = {}) =>
    !shouldCountGazByPiece(
      selectedData.CATEGORY, selectedData.CALCULATOR, product.product_group, product.category
    ) && (
      isRegionalGikPriceListSource(selectedData.SOURCE || product.source) ||
      shouldRoundGazQuantity(
        selectedData.CATEGORY,
        selectedData.CALCULATOR,
        product.product_group,
        product.category
      )
    );

  const shouldUseStrictGazPalletUnitsForProduct = (selectedData = {}, product = {}) =>
    !shouldCountGazByPiece(
      selectedData.CATEGORY, selectedData.CALCULATOR, product.product_group, product.category
    ) && (
      isRegionalGikPriceListSource(selectedData.SOURCE || product.source) ||
      shouldUseStrictGazPalletUnits(
        selectedData.CATEGORY,
        selectedData.CALCULATOR,
        product.product_group,
        product.category
      )
    );

  const normalizeOptionName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/ё/g, "е")
      .replace(/х/g, "x")
      .replace(/\s+/g, " ");

  // options — это словарь на тысячи позиций (вся уникальная номенклатура).
  // Раньше промах по точному имени (например, из-за х/x или лишнего пробела)
  // запускал Object.keys(options).find() — полный проход с нормализацией
  // каждого ключа на КАЖДЫЙ вызов. При нескольких товарах в заказе и
  // нескольких местах вызова за один рендер это давало заметную паузу до
  // отрисовки спиннера. Индекс строим один раз на изменение options.
  const normalizedOptionsIndex = useMemo(() => {
    const index = new Map();
    if (options) {
      Object.keys(options).forEach((key) => {
        const normalized = normalizeOptionName(key);
        if (!index.has(normalized)) {
          index.set(normalized, key);
        }
      });
    }
    return index;
  }, [options]);

  const findOptionByProductName = (name) => {
    if (!name || !options) {
      return null;
    }

    if (options[name]) {
      return options[name];
    }

    const matchedKey = normalizedOptionsIndex.get(normalizeOptionName(name));
    return matchedKey ? options[matchedKey] : null;
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const parsedValue = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const getEffectiveQuantityValue = (value) =>
    value === null || value === undefined || value === "" ? 1 : value;

  const logPalletDebug = (stage, payload) => {
    if (!isCalcDebugEnabled()) {
      return;
    }
    console.log(`[PALLET_DEBUG][ProductsLogic] ${stage}`, payload);
  };

  const parseDensityValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const normalized = String(value).toLowerCase().replace(",", ".");
    const match = normalized.match(/\d+(?:\.\d+)?/);
    if (!match) {
      return null;
    }

    const parsedValue = parseFloat(match[0]);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const formatCalculatedValue = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return "-";
    }

    return numericValue.toFixed(6).replace(/\.?0+$/, "");
  };

  const isCubicMeterUnit = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/³/g, "3")
      .replace(/\s+/g, "");

    return normalized === "м3" || normalized === "м.куб" || normalized === "мкуб";
  };

  const getUnitsPerPalletValue = (selectedData, palletUnits, product = {}) => {
    const numberPerPallet = parseNumericValue(
      selectedData.number_per_pallet ??
      selectedData.NUMBER_PER_PALLET ??
      selectedData.UNITS_PER_PALLET
    );
    const shouldUseOnlyNumberPerPallet = isNumberPerPalletCategory(selectedData, product);
    let resolvedValue = null;
    let resolutionSource = "none";

    if (shouldUseOnlyNumberPerPallet) {
      resolvedValue = numberPerPallet !== null && numberPerPallet > 0 ? numberPerPallet : null;
      resolutionSource = "strict_number_per_pallet";
      logPalletDebug("getUnitsPerPalletValue", {
        productName: product.name || selectedData.NOMENCLATURE || "-",
        paymentMethod,
        region,
        source: selectedData.SOURCE || product.source || null,
        category: selectedData.CATEGORY || product.product_group || null,
        calculator: selectedData.CALCULATOR || product.category || null,
        baseUnit: selectedData.BASE_UNIT || product.unit || null,
        raw: {
          number_per_pallet: selectedData.number_per_pallet ?? null,
          NUMBER_PER_PALLET: selectedData.NUMBER_PER_PALLET ?? null,
          UNITS_PER_PALLET: selectedData.UNITS_PER_PALLET ?? null,
          PALLET_UNITS: selectedData.PALLET_UNITS ?? null,
          pallet_per_unit: selectedData.pallet_per_unit ?? null,
        },
        parsed: {
          numberPerPallet,
          palletUnits,
        },
        shouldUseOnlyNumberPerPallet,
        resolutionSource,
        resolvedValue,
      });
      return resolvedValue;
    }

    const isCubicMeter = isCubicMeterUnit(selectedData.BASE_UNIT);

    if (!isCubicMeter && numberPerPallet !== null && numberPerPallet > 0) {
      resolvedValue = numberPerPallet;
      resolutionSource = "number_per_pallet";
    } else if (isCubicMeter) {
      if (palletUnits !== null && palletUnits > 0) {
        resolvedValue = palletUnits;
        resolutionSource = "pallet_units";
      } else if (numberPerPallet !== null && numberPerPallet > 0) {
        resolvedValue = numberPerPallet;
        resolutionSource = "number_per_pallet_fallback";
      }
    } else {
      const unitsPerPallet = parseNumericValue(selectedData.UNITS_PER_PALLET);
      if (unitsPerPallet !== null && unitsPerPallet > 0) {
        resolvedValue = unitsPerPallet;
        resolutionSource = "units_per_pallet";
      } else if (palletUnits !== null && palletUnits > 0) {
        resolvedValue = palletUnits;
        resolutionSource = "pallet_units";
      } else if (numberPerPallet !== null && numberPerPallet > 0) {
        resolvedValue = numberPerPallet;
        resolutionSource = "number_per_pallet_fallback";
      }
    }

    logPalletDebug("getUnitsPerPalletValue", {
      productName: product.name || selectedData.NOMENCLATURE || "-",
      paymentMethod,
      region,
      source: selectedData.SOURCE || product.source || null,
      category: selectedData.CATEGORY || product.product_group || null,
      calculator: selectedData.CALCULATOR || product.category || null,
      baseUnit: selectedData.BASE_UNIT || product.unit || null,
      raw: {
        number_per_pallet: selectedData.number_per_pallet ?? null,
        NUMBER_PER_PALLET: selectedData.NUMBER_PER_PALLET ?? null,
        UNITS_PER_PALLET: selectedData.UNITS_PER_PALLET ?? null,
        PALLET_UNITS: selectedData.PALLET_UNITS ?? null,
        pallet_per_unit: selectedData.pallet_per_unit ?? null,
      },
      parsed: {
        numberPerPallet,
        palletUnits,
      },
      shouldUseOnlyNumberPerPallet,
      isCubicMeter,
      resolutionSource,
      resolvedValue,
    });

    return resolvedValue;
  };

  const getItemsPerPalletValue = (selectedData) => {
    const itemsPerPallet = parseNumericValue(
      selectedData.number_per_pallet ??
      selectedData.NUMBER_PER_PALLET ??
      selectedData.UNITS_PER_PALLET
    );

    return itemsPerPallet !== null && itemsPerPallet > 0 ? itemsPerPallet : null;
  };

  const getGazobetonMetrics = (quantityValue, selectedData, product = {}, overrides = {}) => {
    const basePalletUnits = parseNumericValue(selectedData.PALLET_UNITS);
    const quantity = quantityValue === "" ? 0 : parseFloat(quantityValue) || 0;
    const resolvedUnitsPerPallet = getUnitsPerPalletValue(selectedData, basePalletUnits, product);
    const itemsPerPalletUnit = getItemsPerPalletValue(selectedData);
    const shouldRoundQuantity = shouldRoundGazProductQuantity(selectedData, product);
    const shouldUseStrictPalletUnits = shouldUseStrictGazPalletUnitsForProduct(selectedData, product);
    const quantityPerPallet = shouldUseStrictPalletUnits && basePalletUnits !== null && basePalletUnits > 0
      ? basePalletUnits
      : resolvedUnitsPerPallet;
    const shouldCalculateItemsSeparately = shouldUseStrictPalletUnits && itemsPerPalletUnit;

    if ((!quantityPerPallet || quantityPerPallet <= 0) && shouldRoundQuantity) {
      logPalletDebug("getGazobetonMetrics:empty", {
        productName: product.name || selectedData.NOMENCLATURE || "-",
        quantityValue,
        quantity,
        source: selectedData.SOURCE || product.source || null,
        raw: {
          number_per_pallet: selectedData.number_per_pallet ?? null,
          NUMBER_PER_PALLET: selectedData.NUMBER_PER_PALLET ?? null,
          UNITS_PER_PALLET: selectedData.UNITS_PER_PALLET ?? null,
          PALLET_UNITS: selectedData.PALLET_UNITS ?? null,
        },
        resolvedUnitsPerPallet,
        quantityPerPallet,
      });
      return {
        factVolume: "-",
        numberPallets: "-",
        itemsPerPallet: "-",
        palletUnits: "-",
        quantity,
      };
    }

    const manualNumberPallets = product?._manualNumberPallets
      ? parseNumericValue(product.numberPallets)
      : null;
    const manualItemsCount = product?._manualItemsPerPallet
      ? parseNumericValue(product.itemsPerPallet)
      : null;
    const safeQuantityPerPallet = quantityPerPallet && quantityPerPallet > 0 ? quantityPerPallet : 1;
    const numberPallets =
      overrides.numberPallets ??
      manualNumberPallets ??
      Math.ceil(quantity / safeQuantityPerPallet);

    const itemsCount =
      overrides.itemsCount ??
      manualItemsCount ??
      (shouldRoundQuantity ? (numberPallets * safeQuantityPerPallet) : quantity);

    const normalizedNumberPallets = Math.max(Number(numberPallets) || 0, 0);
    const normalizedItemsCount = Math.max(Number(itemsCount) || 0, 0);
    const factQuantity = shouldRoundQuantity
      ? normalizedNumberPallets * safeQuantityPerPallet
      : quantity;

    logPalletDebug("getGazobetonMetrics", {
      productName: product.name || selectedData.NOMENCLATURE || "-",
      quantityValue,
      quantity,
      overrides,
      shouldRoundQuantity,
      shouldUseStrictPalletUnits,
      source: selectedData.SOURCE || product.source || null,
      baseUnit: selectedData.BASE_UNIT || product.unit || null,
      raw: {
        number_per_pallet: selectedData.number_per_pallet ?? null,
        NUMBER_PER_PALLET: selectedData.NUMBER_PER_PALLET ?? null,
        UNITS_PER_PALLET: selectedData.UNITS_PER_PALLET ?? null,
        PALLET_UNITS: selectedData.PALLET_UNITS ?? null,
      },
      manual: {
        numberPallets: manualNumberPallets,
        itemsCount: manualItemsCount,
      },
      calculated: {
        resolvedUnitsPerPallet,
        itemsPerPalletUnit,
        quantityPerPallet: safeQuantityPerPallet,
        numberPallets: normalizedNumberPallets,
        itemsPerPallet: normalizedItemsCount,
        factQuantity,
      },
    });

    return {
      factVolume: formatCalculatedValue(factQuantity),
      numberPallets: normalizedNumberPallets,
      itemsPerPallet: shouldCalculateItemsSeparately
        ? formatCalculatedValue(normalizedNumberPallets * itemsPerPalletUnit)
        : normalizedItemsCount,
      palletUnits: formatCalculatedValue(safeQuantityPerPallet),
      quantity,
    };
  };

  const buildProductStateFromSelection = useCallback((product, productName, quantityValue, overrides = {}) => {
    const selectedData = findOptionByProductName(productName) || {};
    const isGazobetonProduct = isGazDeliveryProduct(selectedData, product);
    const gazobetonMetrics = getGazobetonMetrics(quantityValue, selectedData, {
      _manualItemsPerPallet: product?._manualItemsPerPallet,
      itemsPerPallet: product?.itemsPerPallet,
    });
    const weightValue = selectedData.WEIGHT_TONS
      ? parseFloat(String(selectedData.WEIGHT_TONS).replace(",", "."))
      : "-";
    const volumeValue = selectedData.VOLUME_CUBIC
      ? parseFloat(String(selectedData.VOLUME_CUBIC).replace(",", "."))
      : "-";

    return {
      ...product,
      category: selectedData.CALCULATOR || "-",
      product_group: selectedData.CATEGORY,
      name: productName,
      unit: selectedData.BASE_UNIT || "-",
      weight: weightValue !== "-" && !isNaN(weightValue) ? weightValue.toFixed(6) : "-",
      volume: volumeValue !== "-" && !isNaN(volumeValue) ? volumeValue.toFixed(6) : "-",
      bitrixId: selectedData.BITRIX_ID || "",
      width: parseNumericValue(selectedData.WIDTH),
      height: parseNumericValue(selectedData.HEIGHT),
      density: parseDensityValue(selectedData.DENSITY_REPEAT),
      factVolume: isGazobetonProduct ? gazobetonMetrics.factVolume : "-",
      numberPallets: isGazobetonProduct ? gazobetonMetrics.numberPallets : "-",
      itemsPerPallet: isGazobetonProduct ? gazobetonMetrics.itemsPerPallet : "-",
      palletUnits: isGazobetonProduct ? gazobetonMetrics.palletUnits : "-",
      source: selectedData.SOURCE || "-",
      _manualNumberPallets: false,
      _manualItemsPerPallet: false,
      ...overrides,
    };
  }, [findOptionByProductName, getGazobetonMetrics, isGazDeliveryProduct]);

  // Получаем ограничения для выбора товаров
  const getProductRestrictions = useMemo(() => {
    if (!products || products.length === 0) return { hasNonmetallic: false, shouldRestrictToNonmetallic: false, shouldExcludeNonmetallic: false };

    // Проверяем первый товар с заполненным наименованием
    const firstFilledProduct = products.find(product => product.name && product.name !== "");

    if (!firstFilledProduct) {
      return { hasNonmetallic: false, shouldRestrictToNonmetallic: false, shouldExcludeNonmetallic: false };
    }

    const firstProductData = options[firstFilledProduct.name];
    if (!firstProductData || !firstProductData.CALCULATOR) {
      return { hasNonmetallic: false, shouldRestrictToNonmetallic: false, shouldExcludeNonmetallic: false };
    }

    const isFirstNonmetallic = firstProductData.CALCULATOR === "Нерудные материалы";

    return {
      hasNonmetallic: isFirstNonmetallic,
      shouldRestrictToNonmetallic: isFirstNonmetallic, // Если первый - нерудка, показываем только нерудку
      shouldExcludeNonmetallic: !isFirstNonmetallic // Если первый НЕ нерудка, исключаем нерудку
    };
  }, [products, options, paymentMethod]);

  const calculateTotalWeight = useMemo(() => {
    const total = products.reduce((sum, product) => {
      const weight = product.weight !== "-" ? parseFloat(product.weight) || 0 : 0;
      const quantity = product.quantity ? parseFloat(product.quantity) || 0 : 0;
      return isNaN(sum + weight * quantity) || parseFloat(sum + weight * quantity) === 0.0 ? NaN : sum + weight * quantity;
    }, 0);
    return isNaN(total) ? null : total.toFixed(2);
  }, [products]);

  const calculateTotalVolume = useMemo(() => {
    const total = products.reduce((sum, product) => {
      const factVolume = !isNaN(parseFloat(product.factVolume)) ? parseFloat(product.factVolume) : 0;
      return sum + factVolume;
    }, 0);
    return total === 0 ? null : total.toFixed(2);
  }, [products]);

  // Обработчик изменения товара с задержкой, чтобы сравнения не стартовали до ввода количества
  const handleDropdownChange = useCallback((value, productId) => {
    if (!options[value]) {
      console.warn(`⚠️ Нет данных в options для value=${value}`);
      return;
    }

    const existingTimeout = debounceTimeoutsRef.current.get(`dropdown_${productId}`);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    setProducts((prev) =>
      prev.map((product) => {
        if (product.id !== productId) return product;

        const effectiveQuantity = getEffectiveQuantityValue(product.quantity);
        return buildProductStateFromSelection(product, value, effectiveQuantity, {
          _isLoading: false,
          _isTemporary: true,
        });
      })
    );

    const timeoutId = setTimeout(() => {
      setProducts((prev) =>
        prev.map((product) => {
          if (product.id !== productId) return product;

          const effectiveQuantity = getEffectiveQuantityValue(product.quantity);
          return buildProductStateFromSelection(product, value, effectiveQuantity, {
            _isLoading: false,
            _isTemporary: false,
          });
        })
      );

      debounceTimeoutsRef.current.delete(`dropdown_${productId}`);
    }, 1000);

    debounceTimeoutsRef.current.set(`dropdown_${productId}`, timeoutId);
  }, [
    options,
    setProducts,
    buildProductStateFromSelection,
    getEffectiveQuantityValue
  ]);

  // Функция для немедленного обновления UI (без debounce)
  const handlePriceChangeImmediate = useCallback((value, productId) => {
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id !== productId) return product;

        const selectedData = findOptionByProductName(product.name) || {};
        const isGazobetonProduct = isGazDeliveryProduct(selectedData, product);
        const gazobetonMetrics = getGazobetonMetrics(value, selectedData, {
          _manualItemsPerPallet: product?._manualItemsPerPallet,
          itemsPerPallet: product?.itemsPerPallet,
        });
        logPalletDebug("handlePriceChangeImmediate", {
          productId,
          inputValue: value,
          productBefore: product,
          selectedData,
          gazobetonMetrics,
        });
        return {
          ...product,
          quantity: value === "" ? null : value, // Устанавливаем null для пустого значения
          factVolume: isGazobetonProduct ? gazobetonMetrics.factVolume : "-",
          numberPallets: isGazobetonProduct ? gazobetonMetrics.numberPallets : "-",
          itemsPerPallet: isGazobetonProduct ? gazobetonMetrics.itemsPerPallet : "-",
          palletUnits: isGazobetonProduct ? gazobetonMetrics.palletUnits : "-",
          _manualNumberPallets: false,
          _manualItemsPerPallet: false,
          _isTemporary: true, // Помечаем как временное изменение
        };
      })
    );
  }, [options, setProducts, paymentMethod]);

  // Debounced версия для финального обновления (которая запустит API запросы)
  const handlePriceChange = useCallback((value, productId) => {
    // Сначала немедленно обновляем UI
    handlePriceChangeImmediate(value, productId);

    // Очищаем предыдущий таймер только для текущей строки
    const existingTimeout = debounceTimeoutsRef.current.get(productId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Устанавливаем новый таймер для финального обновления
    const timeoutId = setTimeout(() => {
      setProducts((prev) =>
        prev.map((product) => {
          if (product.id !== productId) return product;

          const selectedData = findOptionByProductName(product.name) || {};
          const isGazobetonProduct = isGazDeliveryProduct(selectedData, product);
          const gazobetonMetrics = getGazobetonMetrics(value, selectedData, {
            _manualItemsPerPallet: product?._manualItemsPerPallet,
            itemsPerPallet: product?.itemsPerPallet,
          });

          return {
            ...product,
            quantity: value === "" ? null : value, // Устанавливаем null для пустого значения
            factVolume: isGazobetonProduct ? gazobetonMetrics.factVolume : "-",
            numberPallets: isGazobetonProduct ? gazobetonMetrics.numberPallets : "-",
            itemsPerPallet: isGazobetonProduct ? gazobetonMetrics.itemsPerPallet : "-",
            palletUnits: isGazobetonProduct ? gazobetonMetrics.palletUnits : "-",
            _manualNumberPallets: false,
            _manualItemsPerPallet: false,
            _isLoading: false,
            _isTemporary: false, // Убираем временный флаг
          };
        })
      );

      const existingDropdownTimeout = debounceTimeoutsRef.current.get(`dropdown_${productId}`);
      if (existingDropdownTimeout) {
        clearTimeout(existingDropdownTimeout);
        debounceTimeoutsRef.current.delete(`dropdown_${productId}`);
      }

      debounceTimeoutsRef.current.delete(productId);
    }, 1000);

    debounceTimeoutsRef.current.set(productId, timeoutId);
  }, [handlePriceChangeImmediate, options, setProducts, paymentMethod]);

  /**
   * Количество утеплителя, введённое в м² или м³.
   *
   * В расчёт всё равно уходят упаковки — на них завязаны цена, масса, объём и
   * доставка. Введённое число и выбранная мера остаются при строке отдельными
   * полями: без них поле прыгало бы на пересчитанное значение прямо во время
   * набора, и дописать второй знак стало бы невозможно.
   */
  const handleQuantityUnitInput = useCallback((rawValue, unit, productId, metrics) => {
    setProducts((prev) => prev.map((product) => (
      product.id === productId
        ? { ...product, _quantityInput: rawValue, _quantityUnit: unit }
        : product
    )));

    // Мера приходит из строки таблицы: там позиция уже найдена в справочнике
    // нестрогим поиском по имени, и повторять его здесь значило бы разойтись с
    // тем, что видит менеджер, на позициях с неточным названием.
    const packages = packagesFor(rawValue, unit, metrics);

    // Пересчитать нечем — количество не трогаем. Обнулить его значило бы стереть
    // строку заказа из-за опечатки в поле ввода.
    if (packages === null) {
      if (String(rawValue || "").trim() === "") handlePriceChange("", productId);
      return;
    }

    handlePriceChange(String(packages), productId);
  }, [setProducts, handlePriceChange]);


  const handleGazobetonPalletChange = useCallback((value, productId) => {
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id !== productId) return product;

        const selectedData = findOptionByProductName(product.name) || {};
        const isGazobetonProduct = isGazDeliveryProduct(selectedData, product);
        if (!isGazobetonProduct) {
          return product;
        }

        const parsedPallets = value === "" ? null : parseNumericValue(value);
        if (parsedPallets === null) {
          return {
            ...product,
            quantity: null,
            factVolume: "-",
            numberPallets: "-",
            itemsPerPallet: "-",
            palletUnits: getUnitsPerPalletValue(selectedData, parseNumericValue(selectedData.PALLET_UNITS), product) ?? "-",
            _manualNumberPallets: false,
            _manualItemsPerPallet: false,
            _isTemporary: false,
          };
        }

        if (parsedPallets <= 0) {
          return {
            ...product,
            quantity: 0,
            factVolume: "0",
            numberPallets: 0,
            itemsPerPallet: 0,
            palletUnits: formatCalculatedValue(getUnitsPerPalletValue(selectedData, parseNumericValue(selectedData.PALLET_UNITS), product) || 0),
            _manualNumberPallets: true,
            _manualItemsPerPallet: false,
            _isTemporary: false,
          };
        }
        const gazobetonMetrics = getGazobetonMetrics(product.quantity, selectedData, product, {
          numberPallets: parsedPallets,
        });
        logPalletDebug("handleGazobetonPalletChange", {
          productId,
          inputValue: value,
          parsedPallets,
          productBefore: product,
          selectedData,
          gazobetonMetrics,
        });
        return {
          ...product,
          quantity: gazobetonMetrics.factVolume,
          factVolume: gazobetonMetrics.factVolume,
          numberPallets: gazobetonMetrics.numberPallets,
          itemsPerPallet: gazobetonMetrics.itemsPerPallet,
          palletUnits: gazobetonMetrics.palletUnits,
          _manualNumberPallets: true,
          _manualItemsPerPallet: false,
          _isTemporary: false,
        };
      })
    );
  }, [options, setProducts]);

  const handleGazobetonItemsPerPalletChange = useCallback((value, productId) => {
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id !== productId) return product;

        const selectedData = findOptionByProductName(product.name) || {};
        const isGazobetonProduct = isGazDeliveryProduct(selectedData, product);
        if (!isGazobetonProduct) {
          return product;
        }

        const parsedItemsPerPallet = value === "" ? null : parseNumericValue(value);
        if (parsedItemsPerPallet === null) {
          return {
            ...product,
            quantity: null,
            factVolume: "-",
            numberPallets: "-",
            itemsPerPallet: "-",
            palletUnits: getUnitsPerPalletValue(selectedData, parseNumericValue(selectedData.PALLET_UNITS), product) ?? "-",
            _manualNumberPallets: false,
            _manualItemsPerPallet: false,
            _isTemporary: false,
          };
        }

        if (parsedItemsPerPallet !== null && parsedItemsPerPallet <= 0) {
          return {
            ...product,
            quantity: 0,
            factVolume: "0",
            numberPallets: 0,
            itemsPerPallet: 0,
            palletUnits: formatCalculatedValue(getUnitsPerPalletValue(selectedData, parseNumericValue(selectedData.PALLET_UNITS), product) || 0),
            _manualNumberPallets: false,
            _manualItemsPerPallet: true,
            _isTemporary: false,
          };
        }
        const basePalletUnits = parseNumericValue(selectedData.PALLET_UNITS);
        const quantityPerPallet = shouldUseStrictGazPalletUnitsForProduct(selectedData, product)
          ? basePalletUnits
          : getUnitsPerPalletValue(selectedData, basePalletUnits, product);
        const itemsPerPalletUnit = getItemsPerPalletValue(selectedData) || quantityPerPallet;
        const derivedNumberPallets =
          itemsPerPalletUnit && itemsPerPalletUnit > 0
            ? Math.ceil(parsedItemsPerPallet / itemsPerPalletUnit)
            : 0;
        const nextProduct = {
          ...product,
          _manualItemsPerPallet: true,
          itemsPerPallet: parsedItemsPerPallet,
        };
        const gazobetonMetrics = getGazobetonMetrics(product.quantity, selectedData, nextProduct, {
          itemsCount: parsedItemsPerPallet,
          numberPallets: derivedNumberPallets,
        });
        logPalletDebug("handleGazobetonItemsPerPalletChange", {
          productId,
          inputValue: value,
          parsedItemsPerPallet,
          itemsPerPalletUnit,
          derivedNumberPallets,
          productBefore: product,
          selectedData,
          gazobetonMetrics,
        });

        return {
          ...product,
          quantity: gazobetonMetrics.factVolume,
          factVolume: gazobetonMetrics.factVolume,
          numberPallets: gazobetonMetrics.numberPallets,
          itemsPerPallet: gazobetonMetrics.itemsPerPallet,
          palletUnits: gazobetonMetrics.palletUnits,
          _manualNumberPallets: false,
          _manualItemsPerPallet: true,
          _isTemporary: false,
        };
      })
    );
  }, [options, setProducts]);

  const addRow = () => {
    if (products.length >= 20) {
      alert("Максимальное количество строк достигнуто");
      return;
    }

    const newId = products.length ? products[products.length - 1].id + 1 : 1;

    // Если это первая строка, добавляем её с дефолтными значениями
    if (products.length === 0) {
      setProducts([
        {
          id: newId,
          category: "-",
          quantity: null, // Изменяем с "" на null для консистентности
          name: "",
          unit: "-",
          weight: "-",
          volume: "-",
          factVolume: "-",
          numberPallets: "-",
          itemsPerPallet: "-",
          palletUnits: "-",
          width: null,
          height: null,
          density: null,
          source: "-",
        },
      ]);
      return;
    }

    // Получаем последнюю строку
    const lastProduct = products[products.length - 1];

    // Создаём новую строку, копируя структуру предыдущей, но с заменой значений на "-"
    const newProduct = {
      id: newId,
      category: lastProduct.category !== "-" ? "-" : "-",
      quantity: null, // Изменяем с "" на null для консистентности
      name: lastProduct.name !== "" ? "" : "",
      unit: lastProduct.unit !== "-" ? "-" : "-",
      weight: lastProduct.weight !== "-" ? "-" : "-",
      volume: lastProduct.volume !== "-" ? "-" : "-",
      factVolume: lastProduct.factVolume !== "-" ? "-" : "-",
      numberPallets: lastProduct.numberPallets !== "-" ? "-" : "-",
      itemsPerPallet: lastProduct.itemsPerPallet !== "-" ? "-" : "-",
      palletUnits: lastProduct.palletUnits !== "-" ? "-" : "-",
      width: null,
      height: null,
      density: null,
      bitrixId: lastProduct.bitrixId || "",
      source: lastProduct.source || "",
    };

    setProducts((prev) => [...prev, newProduct]);
  };

  const deleteRow = (productId) => {
    const existingDropdownTimeout = debounceTimeoutsRef.current.get(`dropdown_${productId}`);
    if (existingDropdownTimeout) {
      clearTimeout(existingDropdownTimeout);
    }

    const existingPriceTimeout = debounceTimeoutsRef.current.get(productId);
    if (existingPriceTimeout) {
      clearTimeout(existingPriceTimeout);
    }

    debounceTimeoutsRef.current.delete(`dropdown_${productId}`);
    debounceTimeoutsRef.current.delete(productId);

    if (products.length > 1) setProducts((prev) => prev.filter((product) => product.id !== productId));
    else alert("Невозможно удалить последнюю строку");
  };

  const shouldShowFactVolumeColumn = products.some((product) => {
    const productOption = product?.name ? findOptionByProductName(product.name) : null;
    return isGazDeliveryProduct(productOption, product) || !isNaN(parseFloat(product.factVolume));
  });
  const shouldShowMass = products.some((product) => {
    const productOption = product?.name ? findOptionByProductName(product.name) : null;
    return !isGazDeliveryProduct(productOption, product) && !isNaN(parseFloat(product.weight));
  });
  const shouldShowVol = products.some((product) => {
    const productOption = product?.name ? findOptionByProductName(product.name) : null;
    return !isGazDeliveryProduct(productOption, product) && !isNaN(parseFloat(product.volume));
  });

  // Cleanup таймера при размонтировании
  useEffect(() => {
    return () => {
      debounceTimeoutsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      debounceTimeoutsRef.current.clear();
    };
  }, []);

  return {
    options,
    addRow,
    deleteRow,
    handleDropdownChange,
    handlePriceChange,
    handleQuantityUnitInput,
    handleGazobetonPalletChange,
    handleGazobetonItemsPerPalletChange,
    calculateTotalWeight,
    calculateTotalVolume,
    shouldShowFactVolumeColumn,
    shouldShowMass,
    shouldShowVol,
    getProductRestrictions,
  };
};

export default useProductsLogic;
